import { DEFAULT_CUSTOM_EDGE_CONFIG, DEFAULT_BRANCH_EDGE_CONFIG } from '../constants'
import {
    BranchEdgeData,
    PlaceholderRole,
    UiMetaInvalid,
    isActionJourneyNode,
    isActionStep,
    isSendActionOfType,
    isVariantSplitStep,
    isConditionalStep,
    JourneyEdge,
    JourneyNode,
    JourneyNodeDataType,
    JourneyStepData,
} from '../types/journey-nodes'
import type { IJourneyStep } from '@pushly/models/lib/structs/journeys'
import { getStepsTriggerItemType } from '../../../_utils/journeys'
import { updateStepConfig } from '@pushly/models/lib/structs/journeys'
import { JourneyState } from '../types/journey-context'
import { RecommendationSource } from '@pushly/models/lib/enums/recommendations/recommendation-source'
import { DomainIntegrationProviders } from '@pushly/models/lib/enums/domain-integration-providers'
import { GetBlueConicIntegrationResponse } from '@pushly/models/lib/structs/domains'
import { Journey } from '../types/journey-api-response'
import { toJourneyNotificationPayload } from '../action-helpers'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { JourneyActionType } from '@pushly/models/lib/enums/journeys/journey-action-type'
import { orderSteps } from '@pushly/models/lib/structs/journeys'
import { safeEnumFromValue } from '@pushly/models'
import { getEdgeId } from '../constants'
import { graphHasMeaningfulSteps } from '../types/journey-nodes'
import { isBranchingNode, getBranchingNodeData } from '../utils/graph/traversal'
import { generateStepNames } from './step-name-generator'

/**
 * Transpose nodes from ReactFlow journey back into steps API schema
 */
export const parseJourneyFromFlow = (state: JourneyState): Journey | undefined => {
    const steps: IJourneyStep[] = []
    const { nodes, journey } = state
    if (!journey) {
        return
    }
    const journeyUpdate: Journey = { ...structuredClone(journey), steps: [] }

    for (const node of nodes) {
        const nodeData = node.data.step

        // Branching steps (VARIANT_SPLIT, CONDITION) express branching through branches array.
        // Narrow via step-level type guards so updateStepConfig receives a single union member.
        if (isVariantSplitStep(nodeData)) {
            const branches = nodeData.configuration.branches.map((b) => ({
                kind: b.kind as 'variant',
                key: b.key,
                label: b.label,
                out_step_id: b.out_step_id,
                percentage: b.percentage,
            }))
            const step = updateStepConfig(nodeData, {
                join_step_id: nodeData.configuration.join_step_id,
                branches,
            })
            steps.push({ ...step, campaign_id: !journey ? undefined : journey.id })
            continue
        }

        if (nodeData.type === StepType.CONDITION) {
            // Narrow to IConditionalStep (js-models, array-based branches) — NOT the local
            // ConditionalStep view-model type (tuple-based). Serialization must not assume branch count.
            // The discriminated union on IJourneyStep narrows nodeData to IConditionalStep here.
            const condStep = nodeData
            const branches = condStep.configuration.branches.map((b) => ({
                kind: b.kind as 'condition',
                key: b.key,
                label: b.label,
                out_step_id: b.out_step_id,
            }))
            const step = updateStepConfig(condStep, {
                join_step_id: condStep.configuration.join_step_id,
                branches,
            })
            steps.push({ ...step, campaign_id: !journey ? undefined : journey.id })
            continue
        }

        // API CREATE/PUT step
        const step = updateStepConfig(nodeData, {
            out_step_id: nodeData.configuration.out_step_id,
        })

        // Project action step notifications from rich CDNR to minimal wire format
        if (isActionStep(step)) {
            if (step.configuration.params?.notification) {
                const projected = toJourneyNotificationPayload(
                    step.configuration.params.notification,
                    state.domain,
                )
                step.configuration.params = {
                    ...step.configuration.params,
                    notification: projected,
                }
            }
        }

        steps.push({ ...step, campaign_id: !journey ? undefined : journey.id })
    }

    // Generate deterministic step names before saving. Prefer the trigger's item type (read from the
    // steps being saved) so names match the trigger-aware labels shown in the UI; fall back to the
    // domain's catalog type.
    const triggerItemType = getStepsTriggerItemType(nodes.map((n) => n.data.step))
    const nameMap = generateStepNames(nodes, triggerItemType ?? state.domain.ecommConfig?.itemType)
    for (const step of steps) {
        step.name = nameMap.get(String(step.id))
    }

    journeyUpdate.steps = steps

    return journeyUpdate
}

// region buildInitialJourneyFlow helpers

/**
 * Validates that a recommended notification step's integration reference is still valid.
 * Currently only handles BlueConic — when adding a new recommendation source, add a
 * branch here that checks the new source's integration/configuration integrity.
 */
function applyRecommendedNotificationValidation(
    node: JourneyNode,
    integrations: NonNullable<JourneyState['dependencies']['integrations']>,
): { invalid: { options: string[]; reason: string } } | undefined {
    if (
        !isActionJourneyNode(node) ||
        !isSendActionOfType(node.data.step, JourneyActionType.SEND_RECOMMENDED_NOTIFICATION)
    ) {
        return undefined
    }

    const recommendationSource = node.data.step.configuration.params.recommendation_source
    if (recommendationSource !== RecommendationSource.BLUECONIC) {
        return undefined
    }

    let integrationDisabled = false
    let storeRemoved = false

    const stepIntegration = node.data.step.configuration.params.integration
    const integrationMatch = integrations.get(stepIntegration!.integration_id)

    if (integrationMatch) {
        const storeMatch = (integrationMatch?.data as GetBlueConicIntegrationResponse)?.stores[
            stepIntegration!.store_id
        ]
        if (!storeMatch) {
            storeRemoved = true
        }
    } else {
        integrationDisabled = true
    }

    if (!integrationDisabled && !storeRemoved) {
        return undefined
    }

    const options = ['remove']
    let reason: string
    if (integrationDisabled) {
        reason = 'integration being removed, disabled or incorrectly configured.'
        if (
            Array.from(integrations).some(
                ([_, int]) => int.provider === DomainIntegrationProviders.BLUECONIC_REC,
            )
        ) {
            options.push('edit')
        }
    } else {
        reason = 'store being removed.'
        options.push('edit')
    }

    return {
        invalid: {
            options,
            reason:
                'This step is no longer sending notifications due to the ' +
                reason +
                ' Users in the Journey will be moved to the next step.',
        },
    }
}


/**
 * Create ReactFlow edges for a single step.
 * - Branching steps (VARIANT_SPLIT, CONDITION) → one edge per branch
 * - Linear steps → single edge via out_step_id
 */
function createEdgesForStep(step: IJourneyStep): JourneyEdge[] {
    if (isVariantSplitStep(step) || isConditionalStep(step)) {
        return [...step.configuration.branches]
            .filter((b) => b.out_step_id != null)
            .map((branch) => ({
                ...DEFAULT_BRANCH_EDGE_CONFIG,
                id: `${getEdgeId(step.id)}-${branch.key}`,
                source: step.id.toString(),
                target: branch.out_step_id!.toString(),
                sourceHandle: `branch-${branch.key}`,
                data: {
                    branchKey: branch.key,
                    branchLabel: branch.label,
                    ...(branch.kind === 'variant' ? { percentage: branch.percentage } : {}),
                } as BranchEdgeData,
            }))
    }

    // Linear step — out_step_id exists on all step configurations via IBaseStepConfiguration
    if (step.configuration.out_step_id) {
        return [
            {
                ...DEFAULT_CUSTOM_EDGE_CONFIG,
                id: getEdgeId(step.id),
                source: step.id.toString(),
                target: step.configuration.out_step_id.toString(),
            },
        ]
    }

    return []
}

/** Mark join target nodes for all branching nodes in the graph. Returns new nodes with updated uiMeta. */
function markJoinTargets(nodes: JourneyNode[]): JourneyNode[] {
    // Build a map of joinNodeId → array of owning split/condition node IDs
    const joinTargetMap = new Map<string, string[]>()
    for (const node of nodes) {
        if (isBranchingNode(node)) {
            const data = getBranchingNodeData(node)
            if (data?.join_step_id) {
                const joinId = String(data.join_step_id)
                const existing = joinTargetMap.get(joinId) ?? []
                existing.push(node.id)
                joinTargetMap.set(joinId, existing)
            }
        }
    }

    // Return new nodes with updated uiMeta for join targets
    return nodes.map((node) => {
        const joinTargetOf = joinTargetMap.get(node.id)
        if (!joinTargetOf) return node
        return {
            ...node,
            data: new JourneyStepData({
                step: node.data.step,
                uiMeta: { ...node.data.uiMeta, joinTargetOf },
            }),
        }
    })
}


/**
 * Assign placeholderRole on PLACEHOLDER nodes. Returns new nodes with updated uiMeta.
 *   1. Referenced by a branching node (joinTargetOf non-empty) → 'convergence'
 *   2. Graph has zero meaningful steps → 'empty'
 *   3. Otherwise → orphan placeholder (invalid state)
 */
function assignPlaceholderRoles(nodes: JourneyNode[]): JourneyNode[] {
    const hasMeaningful = graphHasMeaningfulSteps(nodes)

    return nodes.map((node) => {
        if (node.data.step.type !== StepType.PLACEHOLDER) return node

        const joinTargetOf = node.data.uiMeta?.joinTargetOf
        const hasJoinRefs = Array.isArray(joinTargetOf) && joinTargetOf.length > 0

        let placeholderRole: PlaceholderRole | undefined
        let invalid: UiMetaInvalid | undefined = undefined
        if (hasJoinRefs) {
            placeholderRole = 'convergence'
        } else if (!hasMeaningful) {
            placeholderRole = 'empty'
        } else {
            invalid = { reason: 'Orphan placeholder — not referenced by any branching step.' }
        }

        return {
            ...node,
            data: new JourneyStepData({
                step: node.data.step,
                uiMeta: {
                    ...node.data.uiMeta,
                    placeholderRole,
                    ...(placeholderRole === 'convergence' && hasJoinRefs
                        ? { convergenceOf: (joinTargetOf as string[])[0] }
                        : {}),
                    invalid,
                },
            }),
        }
    })
}


// endregion

/**
 * Transpose Journey steps into nodes and edges to be passed to a ReactFlow.
 *
 * @param steps - can be unordered as ordering is conducted during logic
 * @param dependencies - used to determine if integration is valid for a given step
 */
export const buildInitialJourneyFlow = (
    steps: IJourneyStep[],
    dependencies: JourneyState['dependencies'],
): [nodes: JourneyNode[], edges: JourneyEdge[]] => {
    const nodes: JourneyNode[] = []
    const edges: JourneyEdge[] = []

    let actionIndex = 0
    const ordered = orderSteps(steps)

    for (const [idx, step] of ordered.entries()) {
        if (!step.type) continue

        const data = new JourneyStepData<JourneyNodeDataType<IJourneyStep>>({
            step,
            uiMeta: {},
        })

        // Determine node type based on step type
        let nodeType: string = 'custom'
        if (step.type === StepType.VARIANT_SPLIT) {
            nodeType = 'split-step'
        } else if (step.type === StepType.CONDITION) {
            nodeType = 'condition-step'
        }

        let newNode: JourneyNode = {
            id: step.id.toString(),
            position: { x: 0, y: idx * 120 },
            data,
            type: nodeType,
        }

        // global trigger is positioned hidden before the rest of the graph
        if (safeEnumFromValue(StepType, data.step.type) === StepType.GLOBAL_TRIGGER) {
            newNode.hidden = true
            newNode.position = { x: 0, y: -120 }
        }

        // action editor uses the position of the action in the flow to show specific options
        if (isActionJourneyNode(newNode)) {
            newNode.data.step.meta = { ...data.step.meta, actionIndex }
            actionIndex++
        }

        // Validate recommended notification integration references
        if (dependencies.integrations) {
            const blueConicInvalid = applyRecommendedNotificationValidation(newNode, dependencies.integrations)
            if (blueConicInvalid) {
                newNode = {
                    ...newNode,
                    data: new JourneyStepData({
                        step: newNode.data.step,
                        uiMeta: { ...newNode.data.uiMeta, ...blueConicInvalid },
                    }),
                }
            }
        }

        nodes.push(newNode)
        edges.push(...createEdgesForStep(step))
    }

    const markedNodes = markJoinTargets(nodes)
    const finalNodes = assignPlaceholderRoles(markedNodes)

    return [finalNodes, edges]
}
